import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import useInviteesGroupSchema from './InviteesGroup.schema';
import { FileExtension } from 'basics/types/files.types';
import { UseFormDropZoneStateSource } from 'components/QuickForm/FormsBucket/FormDropZone/FormDropZone.enums';
import { AssetTarget, AssetType, Source } from 'generated/event.types';
import { commonFields } from 'modules/events/basics/constants/commonFormFields.constants';
import { InvitationSource } from 'modules/events/basics/enums/events.enums';
import { EventStateType } from 'modules/events/basics/types/events.types';
import { HybridMeetingDataState } from 'modules/events/basics/types/hybridMeeting.types';
import { FILTER_NO_STATUS, FILTER_REJECTED, filterParticipants } from 'modules/events/basics/utils/participants.utils';
import { UploadInviteesProps } from 'modules/events/components/UploadInvitees/UploadInvitees.types';
import eventState from 'modules/events/states/events.states';
import { Icons } from 'yoda-ui/components/Icons/Icon';

type UploadInviteesConfig = Omit<UploadInviteesProps, 'handleFilesExternal' | 'handleRemoveItemAsync'>;

const useInviteesGroupConfig = () => {
  const { t } = useTranslation();
  const inviteesGroupSchema = useInviteesGroupSchema();
  const snap = useSnapshot(eventState) as EventStateType;
  const isRestrictedAccess = (snap.createEvent.data as HybridMeetingDataState)?.isRestrictedAccess;

  const isSourceBackoffice = snap.createEvent?.source === Source.backoffice;

  const cardSwitchConfig = {
    name: commonFields.showSendInvitations,
    title: t('inviteesGroup_cardSwitch_title'),
    defaultValue: snap.createEvent.data?.showSendInvitations || false,
    disabled: isSourceBackoffice,
    ...(isSourceBackoffice && { tooltipTitle: t('inviteesGroup_cardSwitch_tooltip_backoffice') }),
  };

  const uploadInviteesConfig: UploadInviteesConfig = useMemo(
    () => ({
      name: commonFields.participantsWishList,
      formConfig: {
        btnLabel: t('inviteesGroup_tabUpload_btnLabel'),
        btnIcon: Icons.upload,
        maxFileSize: 2048000,
        maxFilesNumber: 0,
        extensionsAllowed: [FileExtension.csv],
        message: t('inviteesGroup_tabUpload_message'),
        label: t('inviteesGroup_tabUpload_label'),
        name: commonFields.participantsWishList,
        assetTarget: AssetTarget.participantCsv,
        assetType: AssetType.document,
        assetsArePublic: true,
        stateSource: UseFormDropZoneStateSource.EXTERNAL,
      },
      listConfig: {
        noItemLabel: t('inviteesGroup_tabUpload_list_noItemLabel'),
        title: t('inviteesGroup_tabUpload_list_title'),
      },
      defaultValue: (
        isRestrictedAccess
          ? snap.createEvent.data?.participants?.filter((participant) => participant.restricted) || []
          : filterParticipants(snap.createEvent.data?.participants || [], FILTER_NO_STATUS, false)
            .concat(filterParticipants(snap.createEvent.data?.participants || [], FILTER_REJECTED, false))
      ).filter((participant) => participant.invitationSource === InvitationSource.BO) || [],
      validation: inviteesGroupSchema.participantsWishList,
      disabled: false,
    }),
    [t, snap.createEvent.data?.participants, inviteesGroupSchema.participantsWishList, isRestrictedAccess],
  );

  return {
    cardSwitchConfig,
    uploadInviteesConfig,
  };
};

export default useInviteesGroupConfig;
